export const getOptions = (
  showTime: boolean = false,
  showYear: boolean = true,
  showDate: boolean = true,
  isUTC: boolean = false
): Intl.DateTimeFormatOptions => {
  return {
    day: showDate ? '2-digit' : undefined,
    month: showDate ? '2-digit' : undefined,
    year: showYear ? 'numeric' : undefined,
    hour: showTime ? '2-digit' : undefined,
    minute: showTime ? '2-digit' : undefined,
    timeZone: isUTC ? 'UTC' : undefined,
  };
};

export const format = (
  date: string,
  options: Intl.DateTimeFormatOptions = getOptions(false)
) => {
  if (date) {
    return new Intl.DateTimeFormat(
      navigator.language ?? 'de-DE',
      options
    ).format(new Date(date));
  }

  return '';
};

export const formatToDifference = (
  dateString?: string,
  i18n: ReturnType<typeof useI18n> = {}
): string => {
  if (!dateString) {
    return '';
  }

  const dateYear = new Date(dateString).getFullYear();
  const currentYear = new Date().getFullYear();

  // 1 or more years ago
  const yearDifference = currentYear - dateYear;
  if (yearDifference > 1) {
    return i18n.t('date-difference.years', { years: yearDifference });
  }

  // 1 or more months ago
  const monthDifference = monthDiff(new Date(dateString), new Date());
  if (monthDifference) {
    return monthDifference === 1
      ? i18n.t('date-difference.one-month')
      : i18n.t('date-difference.months', { months: monthDifference });
  }

  const date = new Date(dateString).getTime();
  const differenceTime = msToTime(Date.now() - date);

  // 1 or more weeks ago
  if (differenceTime.weeks) {
    return differenceTime.days === 1
      ? i18n.t('date-difference.one-week')
      : i18n.t('date-difference.weeks', { weeks: differenceTime.weeks });
  }

  // 1 or more days ago
  if (differenceTime.days) {
    return differenceTime.days === 1
      ? i18n.t('date-difference.one-day')
      : i18n.t('date-difference.days', { days: differenceTime.days });
  }

  // 1 or more hours ago
  if (differenceTime.hours) {
    return differenceTime.hours === 1
      ? i18n.t('date-difference.one-hour')
      : i18n.t('date-difference.hours', { hours: differenceTime.hours });
  }

  // 1 or more minutes ago
  if (differenceTime.minutes) {
    return differenceTime.minutes === 1
      ? i18n.t('date-difference.one-minute')
      : i18n.t('date-difference.minutes', {
          minutes: differenceTime.minutes,
        });
  }

  // Less than one minute
  return i18n.t('date-difference.now');
};

const msToTime = (duration: number) => {
  return {
    milliseconds: Math.floor((duration % 1000) / 100),
    seconds: Math.floor((duration / 1000) % 60),
    minutes: Math.floor((duration / (1000 * 60)) % 60),
    hours: Math.floor((duration / (1000 * 60 * 60)) % 24),
    days: Math.floor((duration / (1000 * 60 * 60 * 24)) % 24),
    weeks: Math.floor((duration / (1000 * 60 * 60 * 24 * 24)) % 4),
  };
};

const monthDiff = (d1: Date, d2: Date): number => {
  var months;
  months = (d2.getFullYear() - d1.getFullYear()) * 12;
  months -= d1.getMonth();
  months += d2.getMonth();
  return months <= 0 ? 0 : months;
};
